import { useCallback, useMemo } from 'react';

import { HomeActions } from '~/pages/Home/redux';

import useGetParentChildrenStates from '../hooks/useGetParentChildrenStates';
import LeftDrawerContext from './LeftDrawerContext';

type LeftDrawerContextProviderProps = {
  children?: React.ReactNode;
};

export function LeftDrawerContextProvider({
  children,
}: Readonly<LeftDrawerContextProviderProps>) {
  const { showParentChildren, setShowParentChildren } =
    useGetParentChildrenStates();

  const handleLeftDrawerCollapseForParentAlert = useCallback(
    (alert, selectedAlert, dispatch) => {
      const hasGutterItemChildren = alert?.gutterItemChildren?.length > 0;
      const doesParentAlertMatch =
        alert?.ruleId === selectedAlert?.ruleId &&
        alert?.displayTitle === selectedAlert?.displayTitle &&
        alert?.parentRuleSortOrder === selectedAlert?.parentRuleSortOrder &&
        alert?.ruleReferenceId === selectedAlert?.ruleReferenceId;

      if (!doesParentAlertMatch && !hasGutterItemChildren) {
        dispatch(HomeActions.setShouldGetNextAlert(false));
        dispatch(HomeActions.setSelectedAlert(alert));
      }

      if (hasGutterItemChildren) {
        setShowParentChildren((prev) => ({
          ...prev,
          [alert?.gutterItemId]: !prev[alert?.gutterItemId],
        }));
      }
    },
    [setShowParentChildren],
  );

  const handleLeftDrawerCollapseForChildAlert = useCallback(
    (alert, selectedAlert, dispatch) => {
      const doesChildAlertMatch =
        alert?.ruleId === selectedAlert?.ruleId &&
        alert?.displayTitle === selectedAlert?.displayTitle &&
        alert?.parentRuleSortOrder === selectedAlert?.parentRuleSortOrder &&
        alert?.ruleReferenceId === selectedAlert?.ruleReferenceId;

      if (!doesChildAlertMatch) {
        dispatch(HomeActions.setSelectedAlert(alert));
        dispatch(HomeActions.setShouldGetNextAlert(false));
      }
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      showParentChildren,
      handleLeftDrawerCollapseForParentAlert,
      handleLeftDrawerCollapseForChildAlert,
    }),
    [
      showParentChildren,
      handleLeftDrawerCollapseForParentAlert,
      handleLeftDrawerCollapseForChildAlert,
    ],
  );

  return (
    <LeftDrawerContext.Provider value={contextValue}>
      {children}
    </LeftDrawerContext.Provider>
  );
}
